import type { SnapshotIn } from "@gadgetinc/mobx-quick-tree";
import {
  Banner,
  BannerKind,
  Body,
  Button,
  ButtonActiveSelector,
  ButtonHoverSelector,
  ButtonKind,
  Column,
  Modal,
  paddingHorizontal,
  paddingUtil,
  paddingVertical,
  PageTitle,
  Pill,
  Row,
} from "@gadgetinc/widgets";
import { AuthMarketingTheme } from "@gadgetinc/widgets/src/AuthTheme";
import { ShopifyIcon } from "@gadgetinc/widgets/src/icons/brands/ShopifyIcon";
import { GitHubIcon } from "@gadgetinc/widgets/src/icons/GitHubIcon";
import { ThirdPartyIcon } from "@gadgetinc/widgets/src/icons/ThirdPartyIcon";
import { ThemeProvider, useStyletron } from "baseui";
import { klona } from "klona";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { type SummonExample } from "state-trees/src/assistant/summon";
import { AppSummonSession } from "../../../client-state-trees/auth/AppSummonSession";
import { InitialSurveySteps } from "../../../client-state-trees/auth/Survey";
import { useIsMobileOrSlimScreen } from "../../../lib/useIsMobile";
import { asyncSafeEventHandler, safelyRunAsyncEventHandler } from "../../../lib/utils";
import { BoundTextArea } from "../../bound-controls/BoundTextArea";
import { AppSummonBootLoaderShell } from "../../edit/workspace/bootloader/EditBootloaderSkeleton";
import { SmallWidth, useGadgetAuthConfig, type GadgetAuthConfig } from "../AuthLayout";
import { CloudflareTurnstile } from "../CloudflareTurnstile";
import { SignInButton } from "../SignInButtons";
import { TechnicalSurvey } from "../survey/TechnicalSurvey";
import { SummonAppChat } from "./SummonAppChat";
import { useCanSummonApp } from "./useCanSummonApp";

interface User {
  id: string;
  email: string;
}

const LocalStorageKey = "summon-app-page-last-prompt";

const SummonAppPage = observer(
  (props: {
    currentUser: User | undefined;
    examples: SummonExample[];
    userEmailHash: string;
    submitImmediately?: boolean;
    summonSession?: SnapshotIn<typeof AppSummonSession> | undefined;
    newUser?: boolean;
    technicalLevel?: string;
  }) => {
    const { currentUser, examples, userEmailHash, summonSession, submitImmediately, newUser, technicalLevel } = props;
    const [signupModalOpen, setSignupModalOpen] = useState(false);
    const canSummonApp = useCanSummonApp();

    const config = useGadgetAuthConfig();

    const canShowSurvey = currentUser && newUser && !technicalLevel;
    const session = useMemo(() => {
      const sessionSnapshot = klona(summonSession) ?? {};

      if (canShowSurvey) {
        sessionSnapshot.survey = {
          userId: String(currentUser.id),
          steps: InitialSurveySteps,
          stepIndex: 0,
        };
      }

      return AppSummonSession.create(sessionSnapshot);
    }, [summonSession, newUser, currentUser, canShowSurvey]);

    const showSurvey = canShowSurvey && session.survey?.step?.key === "technicalLevel";

    const classifyThenSummon = useCallback(() => {
      if (session.loading) {
        return;
      }

      safelyRunAsyncEventHandler(async () => {
        const classified = await session.classify();
        if (classified) {
          await session.summon(userEmailHash, config);
        }
      });
    }, [session, userEmailHash, config]);

    useEffect(() => {
      const lastPrompt = localStorage.getItem(LocalStorageKey);

      if (!lastPrompt || showSurvey) {
        return;
      }

      session.setPrompt(lastPrompt);
      localStorage.removeItem(LocalStorageKey);

      if (currentUser) {
        classifyThenSummon();
      }
    }, [session, currentUser, classifyThenSummon, showSurvey]);

    const onSubmit = useCallback(() => {
      session.setErrorMessage(undefined);

      if (session.promptErrors.length > 0) {
        session.setErrorMessage(session.promptErrors.join(", "));
        session.resetStartedAt();
        return;
      }

      if (!currentUser) {
        if (session.prompt) {
          localStorage.setItem(LocalStorageKey, session.prompt);
        }
        setSignupModalOpen(true);
      } else if (canSummonApp) {
        classifyThenSummon();
      } else {
        session.setErrorMessage("Sorry you don't have access to this feature yet. Please contact support if you think this is an error.");
        session.resetStartedAt();
      }
    }, [currentUser, session, canSummonApp, classifyThenSummon]);

    useEffect(() => {
      if (submitImmediately) {
        onSubmit();
      }
    }, [submitImmediately, onSubmit]);

    const [_, $theme] = useStyletron();

    const { isMobileOrSlim } = useIsMobileOrSlimScreen(SmallWidth);

    const state: "survey" | "signup" | "mobile" | "chat" | "form" = useMemo(() => {
      if (showSurvey && session.survey?.step) {
        return "survey";
      }

      if (signupModalOpen) {
        return "signup";
      }

      if (session.started && isMobileOrSlim) {
        return "mobile";
      }

      if (session.started) {
        return "chat";
      }

      return "form";
    }, [showSurvey, signupModalOpen, session.started, isMobileOrSlim]);

    return (
      <ThemeProvider theme={AuthMarketingTheme}>
        <Helmet>
          <title>Create an app - Gadget</title>
        </Helmet>
        <CloudflareTurnstile protectedEntity={session} />
        <Column
          $style={{
            height: "auto",
            minHeight: "100vh",
            flex: 1,
            backgroundColor: $theme.colors.primary50,
            justifyContent: "center",
          }}
        >
          {state === "survey" ? (
            <TechnicalSurvey step={session.survey!.step!} />
          ) : state === "signup" ? (
            <AppSummonBootLoaderShell fadeIn />
          ) : state === "mobile" ? (
            <ContinueOnDesktop session={session} />
          ) : state === "chat" ? (
            <SummonAppChat session={session} userEmailHash={userEmailHash} />
          ) : (
            <SummonAppForm session={session} onSubmit={onSubmit} examples={examples} />
          )}
        </Column>
        <SignupModal config={config} session={session} isOpen={signupModalOpen} onClose={() => setSignupModalOpen(false)} />
      </ThemeProvider>
    );
  }
);

const SummonAppForm = observer((props: { session: AppSummonSession; onSubmit: () => void; examples: SummonExample[] }) => {
  const { session, onSubmit, examples } = props;
  const [css, $theme] = useStyletron();
  const [promptPlaceholder, setPromptPlaceholder] = useState<string | null>(null);
  const selectedExample = examples.find((example) => example.prompt === session.prompt);

  return (
    <Column
      $gap={$theme.sizing.scale800}
      $style={{
        padding: $theme.sizing.scale600,
        minWidth: "unset",
        margin: "auto",
      }}
    >
      <SummonTitle>Tell us what to build</SummonTitle>
      {session.errorMessage && <Body $style={{ color: $theme.colors.negative }}>{session.errorMessage}</Body>}
      <Column $gap={$theme.sizing.scale600}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              onSubmit();
            }
          }}
        >
          <Column $gap={$theme.sizing.scale500}>
            <BoundTextArea
              dataTestId="summon-app-prompt"
              autoFocus
              placeholder={promptPlaceholder ?? "an app that ..."}
              node={session}
              property="prompt"
              overrides={{ Input: { style: { height: "100px" } } }}
            />
            <Row $gap={$theme.sizing.scale500} $style={{ flexWrap: "wrap" }}>
              {examples.map((example) => (
                <SummonAppExample
                  key={`${example.label}`}
                  example={example}
                  session={session}
                  setPromptPlaceholder={setPromptPlaceholder}
                  selected={selectedExample?.label === example.label}
                />
              ))}
            </Row>
          </Column>
          <Row $style={{ flex: 1, marginTop: $theme.sizing.scale600 }} $align="flex-end" $gap={$theme.sizing.scale300}>
            {session.prompt && session.promptErrors.length > 0 && (
              <Column $gap={$theme.sizing.scale200}>
                {session.promptErrors.map((error) => (
                  <Body key={error} $style={{ color: $theme.colors.negative }}>
                    {error}
                  </Body>
                ))}
              </Column>
            )}
            <Button disabled={!session.canSubmit} data-testid="summon-app-form-submit">
              Create app
            </Button>
          </Row>
        </form>
      </Column>
    </Column>
  );
});

const SummonAppExample = (props: {
  example: SummonExample;
  session: AppSummonSession;
  setPromptPlaceholder: (prompt: string | null) => void;
  selected?: boolean;
}) => {
  const { example, session, setPromptPlaceholder, selected } = props;
  const [_, $theme] = useStyletron();

  return (
    <Pill
      $style={{
        ...paddingHorizontal($theme.sizing.scale300),
        ...paddingVertical($theme.sizing.scale100),
        height: undefined,
        cursor: "pointer",
        userSelect: "none",
        gap: $theme.sizing.scale300,
        ...(selected
          ? {
              outline: `${$theme.borders.border100.borderWidth} solid ${$theme.colors.primary}`,
              boxShadow: $theme.lighting.shadowButtonInset,
            }
          : {
              backgroundColor: $theme.colors.alpha50,
              ":hover": { cursor: "pointer", backgroundColor: $theme.colors.alpha100 },
              ":active": {
                outline: `${$theme.borders.border100.borderWidth} solid ${$theme.colors.alpha300}`,
                boxShadow: $theme.lighting.shadowButtonInset,
              },
            }),
      }}
      onMouseEnter={() => {
        setPromptPlaceholder(example.prompt);
      }}
      onMouseLeave={() => {
        setPromptPlaceholder(null);
      }}
      onClick={() => {
        session.setPrompt(example.prompt);
      }}
    >
      {example.appType === "Shopify" && <ShopifyIcon />}
      <Body>{example.label}</Body>
    </Pill>
  );
};

const SignupModal = observer((props: { isOpen: boolean; onClose: () => void; config: GadgetAuthConfig; session: AppSummonSession }) => {
  const { isOpen, onClose, config, session } = props;
  const [_, $theme] = useStyletron();

  const returnTo = typeof window !== "undefined" ? encodeURIComponent(new URL("/new", window.location.origin).toString()) : "";

  const oAuthQueryParams = useMemo(() => {
    const queryParams = new URLSearchParams();

    if (returnTo) {
      queryParams.set("returnTo", returnTo);
    }

    if (!config.bypassCfTurnstile && session.cfTurnstileToken) {
      queryParams.set("cfTurnstileToken", session.cfTurnstileToken);
    }

    return queryParams.toString();
  }, [config.bypassCfTurnstile, session.cfTurnstileToken, returnTo]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeable={false}
      overrides={{ Dialog: { style: { maxWidth: "90%" } } }}
      footer={
        <Button $as="a" href={`/auth/signup?returnTo=${returnTo}`} kind={ButtonKind.tertiary}>
          Sign up another way
        </Button>
      }
    >
      <Column $gap={$theme.sizing.scale800} $style={{ ...paddingUtil($theme.sizing.scale600), alignItems: "center" }}>
        <Column $gap={$theme.sizing.scale300} $style={{ alignItems: "center" }}>
          <SummonTitle>Get started</SummonTitle>
          <Body>Sign in or create an account to continue</Body>
        </Column>
        <Column $style={{ minWidth: "318px" }} $gap={$theme.sizing.scale300}>
          <SignInButton
            href={`/auth/google`}
            icon={<ThirdPartyIcon type="google" $size={20} />}
            onClick={asyncSafeEventHandler(async (event) => {
              event.preventDefault();

              window.location.href = `/auth/google?${oAuthQueryParams}`;
            })}
            disabled={false}
            $style={{ flex: 1 }}
          >
            Continue with Google
          </SignInButton>
          <SignInButton
            href={`/auth/github`}
            icon={<GitHubIcon $size={20} />}
            onClick={asyncSafeEventHandler(async (event) => {
              event.preventDefault();

              window.location.href = `/auth/github?${oAuthQueryParams}`;
            })}
            disabled={false}
            $style={{ flex: 1 }}
          >
            Continue with Github
          </SignInButton>
        </Column>
      </Column>
    </Modal>
  );
});

const ContinueOnDesktop = observer((props: { session: AppSummonSession }) => {
  const { session } = props;
  const [_, $theme] = useStyletron();
  const [resendDisabled, setResendDisabled] = useState(false);

  const continueOnDesktop = useCallback(() => {
    safelyRunAsyncEventHandler(async () => {
      await session.continueOnDesktop();
    });
  }, [session]);

  const handleResendClick = useCallback(() => {
    setResendDisabled(true);
    continueOnDesktop();

    // Re-enable the button after 10 seconds
    setTimeout(() => {
      setResendDisabled(false);
    }, 10000);
  }, [continueOnDesktop]);

  useEffect(() => {
    continueOnDesktop();
  }, [continueOnDesktop]);

  return (
    <Modal
      isOpen={true}
      closeable={false}
      title={
        <Column $gap={$theme.sizing.scale600}>
          <Banner kind={BannerKind.positive}>
            <Column $gap={$theme.sizing.scale300}>
              <Body $style={{ fontFamily: "Helvetica" }}>{"Your app is ready. Open link sent to email on a desktop to continue."}</Body>

              <Row>
                <Button
                  kind="tertiary"
                  onClick={handleResendClick}
                  disabled={resendDisabled}
                  $style={{
                    textDecoration: "none",
                    color: $theme.colors.contentLinkText,
                    [ButtonHoverSelector]: { color: $theme.colors.contentLinkHover },
                    [ButtonActiveSelector]: { color: $theme.colors.contentLinkActive },
                  }}
                >
                  {resendDisabled ? "Email resent" : "Resend email"}
                </Button>
              </Row>
            </Column>
          </Banner>
          {"Gadget doesn't support mobile"}
        </Column>
      }
    >
      <Column $gap={$theme.sizing.scale600}>
        <Body>{"Gadget is built for desktop use only. Please switch to a larger screen to continue."}</Body>
      </Column>
    </Modal>
  );
});

const SummonTitle = (props: { children: React.ReactNode }) => {
  return <PageTitle $style={{ fontSize: "20px", lineHeight: "25px", fontWeight: 700 }}>{props.children}</PageTitle>;
};

export default SummonAppPage;
